
import { Component, ChangeDetectionStrategy, inject, signal, OnInit, computed, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DataService } from '../../services/data.service';
import { AuthService } from '../../services/auth.service';
import { AppUser, TimeClockEntry, HoursWorkedSummary, ActivityLog } from '../../models/index';
import { EmployeeFormModalComponent } from '../employee-form-modal/employee-form-modal.component';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { InviteEmployeeModalComponent } from '../invite-employee-modal/invite-employee-modal.component';
import { NotificationService } from '../../services/notification.service';
import { ConfirmationModalComponent } from '../confirmation-modal/confirmation-modal.component';

type LoadingState = 'loading' | 'loaded' | 'error';
type EmployeeView = 'list' | 'time-card' | 'hours-worked' | 'logs';

@Component({
  selector: 'app-employees-management',
  standalone: true,
  templateUrl: './employees-management.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [EmployeeFormModalComponent, InviteEmployeeModalComponent, TranslatePipe, FormsModule, CommonModule, ConfirmationModalComponent],
})
export class EmployeesManagementComponent implements OnInit {
  private dataService = inject(DataService);
  private authService = inject(AuthService);
  private notificationService = inject(NotificationService);

  // --- UI & View State ---
  currentView = signal<EmployeeView>('list');
  loadingState = signal<LoadingState>('loading');
  loadingError = signal<string | null>(null);
  
  // --- Data Signals ---
  users = signal<AppUser[]>([]);
  timeClockEntries = signal<TimeClockEntry[]>([]);
  hoursWorkedSummary = signal<HoursWorkedSummary[]>([]);
  activityLogs = signal<ActivityLog[]>([]);
  
  // --- Search & Filter Signals ---
  searchTerm = signal('');
  roleFilter = signal<string>('all');
  statusFilter = signal<string>('all');
  
  // --- Pagination State ---
  currentPage = signal(1);
  pageSize = signal(15);

  // --- Modal State ---
  isUserModalOpen = signal(false);
  isInviteModalOpen = signal(false);
  editingUser = signal<AppUser | null>(null);

  // --- Confirmation Modal State ---
  confirmModalState = signal({
    isOpen: false,
    title: '',
    message: '',
    confirmButtonText: 'Confirm',
    confirmButtonClass: 'bg-red-600 hover:bg-red-700',
    action: null as (() => Promise<void>) | null
  });

  currentUser = this.authService.currentUser;

  // --- COMPUTED: Filtered & Paginated List ---
  
  filteredUsers = computed(() => {
    let list = this.users();
    const term = this.searchTerm().toLowerCase().trim();
    const role = this.roleFilter();
    const status = this.statusFilter();

    if (term) {
      list = list.filter(u => u.name.toLowerCase().includes(term) || u.email.toLowerCase().includes(term));
    }
    if (role !== 'all') {
      list = list.filter(u => u.role === role);
    }
    if (status !== 'all') {
      list = list.filter(u => u.status === status);
    }

    return list;
  });

  paginatedUsers = computed(() => {
    const start = (this.currentPage() - 1) * this.pageSize();
    const end = start + this.pageSize();
    return this.filteredUsers().slice(start, end);
  });

  totalPages = computed(() => Math.ceil(this.filteredUsers().length / this.pageSize()));

  pageInfoText = computed(() => {
    const total = this.filteredUsers().length;
    if (total === 0) return 'No matching employees';
    const start = (this.currentPage() - 1) * this.pageSize() + 1;
    const end = Math.min(this.currentPage() * this.pageSize(), total);
    return `แสดง ${start}-${end} จาก ${total} รายการ`;
  });

  constructor() {
    effect(() => {
        this.searchTerm(); this.roleFilter(); this.statusFilter();
        this.currentPage.set(1);
    }, { allowSignalWrites: true });
  }

  ngOnInit(): void {
    this.loadData();
  }

  async loadData(): Promise<void> {
    this.loadingState.set('loading');
    try {
      if (this.currentView() === 'list') {
        const users = await this.dataService.getUsers();
        this.users.set(users.sort((a, b) => a.role.localeCompare(b.role) || a.name.localeCompare(b.name)));
      }
      this.loadingState.set('loaded');
    } catch (e) {
      this.loadingState.set('error');
      this.loadingError.set((e as any).message);
    }
  }

  switchView(view: EmployeeView): void {
    this.currentView.set(view);
    this.loadData();
  }

  setPage(p: number) {
      if (p >= 1 && p <= this.totalPages()) {
          this.currentPage.set(p);
      }
  }

  // --- Modal Helpers ---
  openInviteModal(): void { this.isInviteModalOpen.set(true); }
  openEditModal(user: AppUser): void { this.editingUser.set(user); this.isUserModalOpen.set(true); }
  handleCloseModal(): void { this.isUserModalOpen.set(false); this.isInviteModalOpen.set(false); }

  async handleSaveUser(userData: AppUser): Promise<void> {
    try {
      await this.dataService.updateUser(userData);
      this.notificationService.showSuccess('อัปเดตข้อมูลพนักงานสำเร็จ');
      this.loadData();
      this.isUserModalOpen.set(false);
    } catch (e: any) {
      this.notificationService.handleError(e);
    }
  }

  deleteUser(userToDelete: AppUser): void {
    if (userToDelete.uid === this.currentUser()?.uid) return;

    this.confirmModalState.set({
        isOpen: true,
        title: 'ยืนยันการลบพนักงาน',
        message: `คุณต้องการลบพนักงาน "${userToDelete.name}" ออกจากระบบใช่หรือไม่?`,
        confirmButtonText: 'ลบพนักงาน (Delete)',
        confirmButtonClass: 'bg-red-600 hover:bg-red-700',
        action: async () => {
            try {
                await this.dataService.deleteUser(userToDelete.uid);
                this.notificationService.showSuccess('ลบพนักงานออกเรียบร้อยแล้ว');
                this.loadData();
            } catch (e: any) {
                this.notificationService.handleError(e);
            }
        }
    });
  }

  closeConfirmModal() {
    this.confirmModalState.update(s => ({ ...s, isOpen: false }));
  }

  async onConfirmAction() {
    const action = this.confirmModalState().action;
    if (action) await action();
    this.closeConfirmModal();
  }

  getInitials(name: string): string {
      return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  }

  getAvatarColor(role: string): string {
      switch(role) {
          case 'owner': return 'bg-amber-500';
          case 'admin': return 'bg-purple-600';
          case 'doctor': return 'bg-blue-600';
          case 'cashier': return 'bg-emerald-600';
          default: return 'bg-slate-500';
      }
  }
}
